import { flatMap } from 'lodash';

import {
    BASELINE_SEARCH_SORT_RANK,
    SearchConfig,
    SearchConfigKeywordGroup,
    SearchKeywordSets
} from 'shared/models/search';
import { SearchPresetData } from 'shared/models/search-preset';
import { HighlightTerms } from '../hooks/use-highlight';
import { keywordSearchPresetGroups } from '../search/search-keywords-requirements';

import { Search, SearchStatus } from '../state';

export const getStatusLabel = (search: Partial<Search>, status: SearchStatus) => {
    if (search.sortRank === BASELINE_SEARCH_SORT_RANK) {
        return 'Baseline';
    }
    switch (status) {
        case SearchStatus.Aborted:
            return 'Aborted';
        case SearchStatus.Active:
            return 'Active';
        case SearchStatus.Caching:
            return 'Caching';
        case SearchStatus.Complete:
            return 'Complete';
        case SearchStatus.Initial:
            return 'Initial';
        case SearchStatus.Paused:
            return 'Paused';
        case SearchStatus.Waiting:
            return 'Waiting for Initialization';
        case SearchStatus.InitFailed:
            return 'Failed to initialize';
        case SearchStatus.TooManyResults:
            return 'Too Many Results';
        case SearchStatus.ScoringProfiles:
            return 'Scoring Profiles';
        case SearchStatus.UpdatingProfiles:
            return 'Updating Profiles';
        case SearchStatus.ResultsCached:
            return 'Results Cached';
        case SearchStatus.Archived:
            return 'Archived';
        case SearchStatus.ScoringFailed:
            return 'Scoring Failed';
        default:
            throw new Error('Unknown search status: ' + status);
    }
};

const replacePresets = (searchPresets: SearchPresetData[], groups: SearchConfigKeywordGroup[]) => {
    return flatMap(
        groups.map((g) =>
            flatMap(
                g.keywords.map((kw) => {
                    const matchingPreset = searchPresets.find(
                        (p) => p.name === kw.label && keywordSearchPresetGroups.includes(p.group)
                    );
                    if (kw.isPreset && !!matchingPreset) {
                        return matchingPreset.list.map((l) => l.label || l.value);
                    } else {
                        return [kw.label];
                    }
                })
            )
        )
    );
};

export const getSearchHighlightTerms = (
    searchPresets: SearchPresetData[],
    searchConfig: SearchConfig,
    tooltip?: string
) => {
    const positiveTerms = searchConfig
        ? replacePresets(
              searchPresets,
              searchConfig.keywordGroups.filter((g) => !g.negative)
          )
        : [];
    const negativeTerms = searchConfig
        ? replacePresets(
              searchPresets,
              searchConfig.keywordGroups.filter((g) => g.negative)
          )
        : [];

    return [
        { className: 'positive', terms: positiveTerms, tooltip },
        { className: 'negative', terms: negativeTerms, tooltip }
    ];
};

/**
 * Merge two HighlightTerms arrays by className (e.g. positive + positive, negative + negative).
 */
export const mergeHighlightTerms = (...termSets: HighlightTerms[][]): HighlightTerms[] => {
    const byClass = new Map<string, HighlightTerms>();
    for (const terms of termSets) {
        for (const t of terms) {
            const existing = byClass.get(t.className);
            if (existing) {
                existing.terms = [...existing.terms, ...t.terms];
            } else {
                byClass.set(t.className, { ...t, terms: [...t.terms] });
            }
        }
    }
    return [...byClass.values()];
};

export const getKeywordSetsHighlightTerms = (keywordSets: SearchKeywordSets, tooltip?: string): HighlightTerms[] => {
    const includeTerms = flatMap(
        Object.values(keywordSets)
            .filter((kw) => kw.type === 'include')
            .map((kw) => kw.terms)
    );
    const excludeTerms = flatMap(
        Object.values(keywordSets)
            .filter((kw) => kw.type === 'exclude')
            .map((kw) => kw.terms)
    );
    return [
        { className: 'positive', terms: includeTerms, tooltip },
        { className: 'negative', terms: excludeTerms, tooltip }
    ];
};
